import { useCallback, useMemo, useState } from "react";

import { BackgroundProps, Box, ColorProps, Flex, useDisclosure } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";

import { InventoryChangeReason } from "__graphql__/types";
import { ItemNotReturnedWarningModal } from "flows/Inbound/components/ItemNotReturnedWarningModal";
import { ProductComplaintMetadata, StockUpdatePlan } from "flows/Inbound/stores/inboundStore/types";
import { getNegativeTotalForReasons, getTotalForReasons } from "flows/Inbound/utils/helpers";
import { Page } from "shared/components/Page";
import { transformChunks } from "shared/components/TextWrapper";
import { BottomTabBarContainer } from "ui/BottomTabBarContainer";
import { Button } from "ui/Button/Button";
import { IncreaseDecrease } from "ui/IncreaseDecrease/IncreaseDecrease";
import { NavigationHeaderShelfNumber } from "ui/NavigationHeaderShelfNumber/NavigationHeaderShelfNumber";
import { ProductInformation } from "ui/ProductInformation/ProductInformation";
import { HeaderM, TitleS } from "ui/Typography/Typography";

type OutboundCounterProps = {
  numberColor?: ColorProps["color"];
  bgColor?: BackgroundProps["bg"];
  value: number;
  title: string;
};

type InboundEditQuantityProps = {
  outboundSku: string;
  shelfLetter: string | null;
  shelfNumber: string | null;
  productSku: string;
  productName: string | null;
  productImageUrl: string | null;
  stockUpdatePlan: StockUpdatePlan[];
  isStockUpdating: boolean;
  updateStock: (
    sku: string,
    quantitiesByReason: StockUpdatePlan[],
    productComplaintMetadata?: ProductComplaintMetadata,
  ) => void;
  returnToInboundHomePage: () => void;
};

function OutboundCounter({ numberColor, bgColor, value, title }: OutboundCounterProps) {
  return (
    <Flex direction="column" alignItems="center">
      <TitleS color="grey.600">{title}</TitleS>
      <HeaderM paddingX="s100" borderRadius="8px" bg={bgColor} color={numberColor} mt="s50">
        {value}
      </HeaderM>
    </Flex>
  );
}

export function InboundEditQuantity({
  outboundSku,
  shelfLetter,
  shelfNumber,
  productSku,
  productName,
  productImageUrl,
  stockUpdatePlan,
  isStockUpdating,
  updateStock,
  returnToInboundHomePage,
}: InboundEditQuantityProps) {
  const intl = useIntl();
  const {
    onOpen: onOpenItemNotReturnedWarningModal,
    isOpen: isItemNotReturnedWarningModalOpen,
    onClose: onCloseItemNotReturnedWarningModal,
  } = useDisclosure();

  const cancellationCount = useMemo(
    () => getTotalForReasons(stockUpdatePlan, [InventoryChangeReason.inbound_order_cancellation]),
    [stockUpdatePlan],
  );
  const initialNotSellable = useMemo(
    () =>
      getNegativeTotalForReasons(stockUpdatePlan, [
        InventoryChangeReason.outbound_order_cancellation_not_sellable,
      ]),
    [stockUpdatePlan],
  );
  const initialNotReturned = useMemo(
    () =>
      getNegativeTotalForReasons(stockUpdatePlan, [
        InventoryChangeReason.outbound_order_cancellation_not_returned,
      ]),
    [stockUpdatePlan],
  );
  const [notSellableCount, setNotSellableCount] = useState(initialNotSellable);
  const [notReturnedCount, setNotReturnedCount] = useState(initialNotReturned);

  const outboundedCount = notReturnedCount + notSellableCount;
  const isIncreaseBtnDisabled = outboundedCount >= cancellationCount;
  const newStockValue = cancellationCount - outboundedCount;

  const confirmOutboundChoices = useCallback(() => {
    const quantitiesByReason = [
      {
        reason: InventoryChangeReason.outbound_order_cancellation_not_sellable,
        quantityDelta: -notSellableCount,
      },
      {
        reason: InventoryChangeReason.outbound_order_cancellation_not_returned,
        quantityDelta: -notReturnedCount,
      },
    ];

    const outboundTotal = quantitiesByReason.reduce((sum, { quantityDelta }) => {
      return sum + Math.abs(quantityDelta);
    }, 0);
    if (outboundTotal > 0) updateStock(outboundSku || "", quantitiesByReason);
    if (!isStockUpdating) returnToInboundHomePage();
  }, [
    isStockUpdating,
    notReturnedCount,
    notSellableCount,
    outboundSku,
    returnToInboundHomePage,
    updateStock,
  ]);

  const handleOnConfirm = useCallback(() => {
    if (newStockValue === 0) {
      onOpenItemNotReturnedWarningModal();
    } else {
      confirmOutboundChoices();
    }
  }, [confirmOutboundChoices, newStockValue, onOpenItemNotReturnedWarningModal]);

  return (
    <Page isFull isBgGrey h="100%">
      <NavigationHeaderShelfNumber
        title={intl.formatMessage({ id: "pages.inbound.edit-quantity.title" })}
        shelfLetter={shelfLetter}
        shelfNumber={shelfNumber}
        onClickGoBack={returnToInboundHomePage}
      />
      <Flex
        direction="column"
        w="100%"
        h="100%"
        flex={1}
        justify="flex-start"
        bg="grey.100"
        overflowX="scroll"
      >
        <Box bg="white" w="full">
          <ProductInformation
            name={productName ?? ""}
            imageUrl={productImageUrl}
            sku={productSku}
            variantSize="s"
            p="s200"
            borderRadius="0"
          />
        </Box>
        <Flex direction="column" w="100%" p="s200" gap="s200">
          <Flex
            alignItems="center"
            justifyContent="space-evenly"
            borderRadius="8px"
            bg="white"
            w="100%"
            p="s200"
          >
            <OutboundCounter
              title={intl.formatMessage({ id: "pages.inbound.edit-quantity.counted-title" })}
              value={cancellationCount}
            />
            <OutboundCounter
              title={intl.formatMessage({
                id: "pages.inbound.edit-quantity.removed-title",
              })}
              value={outboundedCount * -1}
              numberColor="purple.500"
            />
            <OutboundCounter
              title={intl.formatMessage({ id: "pages.inbound.edit-quantity.re-Inbounded-title" })}
              value={newStockValue}
              numberColor="pinkFlink.500"
              bgColor="pinkFlink.100"
            />
          </Flex>
          <Flex direction="column" gap="s50">
            <Box bg="white" borderRadius="8px" p="s200">
              <IncreaseDecrease
                changeHandler={setNotSellableCount}
                label={
                  <FormattedMessage
                    id="pages.inbound.edit-quantity.not-sellable-label"
                    values={transformChunks({ b: { as: "strong" } })}
                  />
                }
                count={notSellableCount}
                isIncreaseBtnDisabled={isIncreaseBtnDisabled}
              />
            </Box>
            <Box bg="white" borderRadius="8px" p="s200">
              <IncreaseDecrease
                changeHandler={setNotReturnedCount}
                label={
                  <FormattedMessage
                    id="pages.inbound.edit-quantity.not-returned-label"
                    values={transformChunks({ b: { as: "strong" } })}
                  />
                }
                count={notReturnedCount}
                isIncreaseBtnDisabled={isIncreaseBtnDisabled}
              />
            </Box>
          </Flex>
        </Flex>
      </Flex>
      <BottomTabBarContainer
        w="100%"
        px="s200"
        bgColor="white"
        borderTopWidth="0.5px"
        borderTopColor="grey.200"
        align="center"
      >
        <Button isFullWidth onClick={handleOnConfirm} isLoading={isStockUpdating}>
          <FormattedMessage id="generic.actions.confirm" />
        </Button>
      </BottomTabBarContainer>
      <ItemNotReturnedWarningModal
        isOpen={isItemNotReturnedWarningModalOpen}
        onClickYes={confirmOutboundChoices}
        onClickNo={onCloseItemNotReturnedWarningModal}
      />
    </Page>
  );
}
